import { Loader2 } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useDeleteAssessment } from "@/lib/api";

interface DeleteAssessmentDialogProps {
  /** The assessment to delete, or null when nothing is selected. */
  assessmentId: string | null;
  /** Named in the confirmation copy so the user sees what they're deleting. */
  companyName: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Runs after a successful delete, once the dialog has closed. The report page
   * navigates away here (the record it renders 404s the moment it's gone); the
   * history page needs nothing: the list invalidation drops the row.
   */
  onDeleted?: () => void;
}

/**
 * Owner-facing confirm-and-delete for a single assessment. Shared by the report
 * page and the history table so both destroy an assessment the same way. The
 * hook is keyed by id, so it's given an empty string while the dialog is closed
 * (no selection): the handler never fires without a real id.
 */
export function DeleteAssessmentDialog({
  assessmentId,
  companyName,
  open,
  onOpenChange,
  onDeleted,
}: DeleteAssessmentDialogProps) {
  const deleteMutation = useDeleteAssessment(assessmentId ?? "");
  const [error, setError] = useState("");

  // One dialog instance is reused for every row on /history, so a close must
  // reset the error; otherwise a failed delete's banner reappears on the next
  // row. A close request while the DELETE is in flight can't abort it, so ignore
  // it rather than leave the UI out of sync with the server.
  const handleOpenChange = (next: boolean) => {
    if (deleteMutation.isPending) {
      return;
    }
    if (!next) {
      setError("");
    }
    onOpenChange(next);
  };

  const handleDelete = async () => {
    if (!assessmentId) {
      return;
    }
    setError("");
    try {
      await deleteMutation.mutateAsync();
      onOpenChange(false);
      onDeleted?.();
    } catch {
      setError("Couldn’t delete this assessment. Please try again.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Delete assessment</DialogTitle>
          <DialogDescription>
            Permanently delete the assessment for <strong>{companyName ?? "this company"}</strong>?
            This erases its report, any expert calibration, its share link, and any invites. This
            action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        {error && <p className="text-destructive text-sm">{error}</p>}
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={deleteMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={() => void handleDelete()}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
